import React, { useEffect, useMemo, useState } from "react";
import "../styles/partnership.css";
import axios from "axios";

const audienceOrder = [
  "School Kids",
  "House Wifes",
  "Active Professionals",
  "College Graduates",
];

const audienceMeta = {
  "School Kids": {
    icon: "🎒",
    subtitle: "Creative, beginner-friendly learning pathways",
  },
  "House Wifes": {
    icon: "🌸",
    subtitle: "Flexible upskilling and restart-focused programs",
  },
  "Active Professionals": {
    icon: "💻",
    subtitle: "Career growth, productivity, and AI-enabled learning",
  },
  "College Graduates": {
    icon: "🎓",
    subtitle: "Job readiness, certifications, and hands-on tracks",
  },
};

const PartnershipPrograms = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ FETCH FROM BFF
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/2.0/partnership-programs`
        );

        if (res.data.success) {
          const list = res.data.data.partners || [];
          setPartners(list);

          if (list.length > 0) {
            setSelectedPartner(list[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching programs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  const selected = selectedPartner;

  // ✅ GROUP PROGRAMS
  const groupedPrograms = useMemo(() => {
    if (!selected) return {};

    const grouped = {};
    audienceOrder.forEach((group) => {
      grouped[group] =
        selected.programs?.filter((p) => p.audience === group) || [];
    });
    return grouped;
  }, [selected]);

  // ✅ LOADING STATES
  if (loading) return <div>Loading programs...</div>;
  if (!selected) return <div>No data available</div>;

  return (
    <div className={`partnership-page ${selected.theme}`}>
      <div className="partnership-glow partnership-glow-one" />
      <div className="partnership-glow partnership-glow-two" />

      {/* HERO */}
      <section className="partnership-hero">
        <div className="partnership-hero-content">
          <span className="hero-pill">AI-Powered Partnership Ecosystem</span>
          <h1>Partnership Programs</h1>

          <p>{selected.description}</p>

          <div className="hero-stats">
            {selected.stats?.map((item, i) => (
              <div key={i} className="hero-stat-card">
                <h3>{item.value}</h3>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="partnership-hero-visual">
          <div className="hero-image-card">
            <img src={selected.image} alt={selected.name} />
            <div className="hero-image-overlay">
              <span className="partner-tag">
                {selected.name} Partner Network
              </span>
              <h3>Future-ready community programs</h3>
              <p>{selected.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* LAYOUT */}
      <div className="partnership-layout">
        {/* SIDEBAR */}
        <aside className="partner-sidebar">
          <h3>Partners</h3>

          <div className="partner-button-list">
            {partners.map((partner) => (
              <button
                key={partner.partner_id}
                className={`partner-btn ${
                  selected.partner_id === partner.partner_id ? "active" : ""
                }`}
                onClick={() => setSelectedPartner(partner)}
              >
                <span className="partner-btn-dot" />
                {partner.name}
              </button>
            ))}
          </div>
        </aside>

        {/* CONTENT */}
        <section className="partner-content">
          <div className="partner-intro-card">
            <div>
              <span className="section-mini-label">Selected Partner</span>
              <h2>{selected.name}</h2>
              <p>{selected.description}</p>
            </div>
          </div>

          {audienceOrder.map((group) => (
            <div key={group} className="audience-section">
              <div className="audience-header">
                <h3>
                  <span className="audience-icon">
                    {audienceMeta[group].icon}
                  </span>
                  {group}
                </h3>
                <p>{audienceMeta[group].subtitle}</p>
              </div>

              <div className="program-grid">
                {groupedPrograms[group]?.map((program) => (
                  <div key={program.program_id} className="program-card">
                    <div className="program-card-top">
                      <div className="program-icon">{program.icon}</div>
                      <span className="program-chip">AI-ready track</span>
                    </div>

                    <h4>{program.title}</h4>
                    <p>{program.details}</p>

                    <div className="program-footer">
                      <span className="audience-badge">
                        {program.audience}
                      </span>
                      <button className="learn-more-btn">
                        Learn More
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
};

export default PartnershipPrograms;